import { useId, useMemo, useRef, useState, type FormEvent } from "react";
import { ApiError } from "@/core/api/apiTypes";
import { FormDefinitionEditor } from "@/features/form-builder/components/FormDefinitionEditor";
import { useBuilderDesktop } from "@/features/form-builder/hooks/useBuilderDesktop";
import { createBlankDraft, readFormDefinition, serializeFormDefinition } from "@froppio/form-core";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormField } from "@/shared/components/ui/FormField";
import { Page, PageContent, PageFooter } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useOperationLifetime } from "@/shared/hooks/useOperationLifetime";
import { FormNameSchema, type CreateFormRequest, type Form } from "../types/formTypes";
import { FormMetadata } from "./FormMetadata";

interface FormEditorProps {
  businessId: string;
  businessName: string;
  initialForm?: Form;
  onSave: (request: CreateFormRequest, expectedVersion?: number) => Promise<Form>;
  onReload?: () => Promise<Form>;
}

export function FormEditor({ businessId, businessName, initialForm, onSave, onReload }: FormEditorProps) {
  const [snapshot, setSnapshot] = useState(initialForm);
  const [name, setName] = useState(initialForm?.name ?? "");
  const [definition, setDefinition] = useState(() => initialForm?.definition ?? serializeFormDefinition(createBlankDraft()));
  const [nameError, setNameError] = useState("");
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState(false);
  const [reloading, setReloading] = useState(false);
  const submitting = useRef(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const captureOperation = useOperationLifetime();
  const { goBack, goToFormDetails } = useAppNavigation();
  const desktop = useBuilderDesktop();
  const supported = useMemo(() => readFormDefinition(definition).success, [definition]);
  const saveFormId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting.current || conflict || !desktop) return;
    if (!supported) {
      setError("Reset this unsupported definition before saving a visual form.");
      return;
    }
    const parsed = FormNameSchema.safeParse(name);
    if (!parsed.success) {
      setNameError(parsed.error.issues[0].message);
      nameRef.current?.focus();
      return;
    }
    submitting.current = true;
    const isCurrent = captureOperation();
    setPending(true);
    setNameError("");
    setError("");
    try {
      const saved = await onSave({ name: parsed.data, definition }, snapshot?.version);
      if (isCurrent()) goToFormDetails(businessId, saved.id, true);
    } catch (cause) {
      if (!isCurrent()) return;
      if (cause instanceof ApiError && cause.Status === 409) setConflict(true);
      else setError(cause instanceof Error ? cause.message : "Unable to save this form.");
    } finally {
      submitting.current = false;
      if (isCurrent()) setPending(false);
    }
  }

  async function reloadLatest() {
    if (!onReload || submitting.current) return;
    submitting.current = true;
    const isCurrent = captureOperation();
    setReloading(true);
    setError("");
    try {
      const latest = await onReload();
      if (!isCurrent()) return;
      setSnapshot(latest);
      setName(latest.name);
      setDefinition(latest.definition);
      setNameError("");
      setConflict(false);
    } catch (cause) {
      if (isCurrent()) setError(cause instanceof Error ? cause.message : "Unable to reload the latest version.");
    } finally {
      submitting.current = false;
      if (isCurrent()) setReloading(false);
    }
  }

  return (
    <Page>
      <PageHeader title={initialForm ? "Edit form" : "Create form"} description={snapshot ? <FormMetadata form={snapshot} /> : businessName}
        actions={<PageHeaderAction onClick={goBack}>Back</PageHeaderAction>} />
      <PageContent className="max-w-[1440px] space-y-[26px]">
        {error && <ErrorState message={error} />}
        {conflict && (
          <Section title="A newer version is available" card>
            <p role="alert" className="text-sm text-muted-foreground">Another member saved this form. Your draft has not been changed. Copy anything you want to keep before reloading; reloading replaces your draft with the latest saved version.</p>
            <Button variant="outline" disabled={reloading} onClick={() => void reloadLatest()}>{reloading ? "Reloading..." : "Reload latest version"}</Button>
          </Section>
        )}
        {!desktop && <p role="status" className="text-sm text-muted-foreground">Editing and saving are available on desktop only. You can preview this form here.</p>}
        <form id={saveFormId} onSubmit={handleSubmit} noValidate>
          <Section title="Form name">
            <fieldset disabled={pending || reloading} className="min-w-0 space-y-[18px]">
              <FormField id="form-name" label="Name" required error={nameError} description="1–100 characters. Surrounding spaces are removed.">
                <Input ref={nameRef} name="name" value={name} required readOnly={!desktop || !supported} onChange={event => { setName(event.target.value); setNameError(""); }} />
              </FormField>
            </fieldset>
          </Section>
        </form>
        <fieldset disabled={pending || reloading} inert={pending || reloading} className="min-w-0">
          <legend className="sr-only">Form definition</legend>
          <FormDefinitionEditor key={snapshot?.version ?? 0} definition={definition} onChange={setDefinition} />
        </fieldset>
      </PageContent>
      <PageFooter>
        <Button variant="outline" onClick={goBack}>Cancel</Button>
        <Button type="submit" form={saveFormId} disabled={pending || reloading || conflict || !desktop || !supported}>{pending ? "Saving..." : initialForm ? "Save new version" : "Create form"}</Button>
      </PageFooter>
    </Page>
  );
}
